import api from "./api";
import type {
  JournalEntry,
  JournalEntryCreate,
  JournalEntryKind,
  HighlightColor,
  JournalBulkOrganization,
  JournalEntryList,
  JournalEntryUpdate,
  ReflectionPrompt,
  ReflectionRevision,
  JournalAttachment,
} from "../types";
import { conditionalHeaders } from "./concurrency";

export interface JournalFilters {
  q?: string;
  kind?: JournalEntryKind;
  library_entry_id?: number;
  author?: string;
  reading_session_id?: number;
  tag_id?: number;
  color?: HighlightColor;
  is_favorite?: boolean;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
  sort?: "occurred_desc" | "occurred_asc" | "updated_desc" | "label_asc";
}

const ENDPOINT = "/journal-entries";

export const journalService = {
  async list(
    filters: JournalFilters = {},
    signal?: AbortSignal
  ): Promise<JournalEntryList> {
    const response = await api.get<JournalEntryList>(ENDPOINT, {
      params: filters,
      signal,
    });
    return response.data;
  },
  async create(payload: JournalEntryCreate): Promise<JournalEntry> {
    const response = await api.post<JournalEntry>(ENDPOINT, payload);
    return response.data;
  },
  async update(
    id: number,
    payload: JournalEntryUpdate,
    updatedAt?: string
  ): Promise<JournalEntry> {
    const response = await api.patch<JournalEntry>(`${ENDPOINT}/${id}`, payload, {
      headers: conditionalHeaders(updatedAt),
    });
    return response.data;
  },
  async delete(id: number, updatedAt?: string): Promise<void> {
    await api.delete(`${ENDPOINT}/${id}`, { headers: conditionalHeaders(updatedAt) });
  },
  async bulkOrganize(payload: JournalBulkOrganization): Promise<JournalEntry[]> {
    const response = await api.post<{ updated: JournalEntry[] }>(
      `${ENDPOINT}/bulk-organize`,
      payload
    );
    return response.data.updated;
  },
  async listReflectionPrompts(signal?: AbortSignal): Promise<ReflectionPrompt[]> {
    const response = await api.get<ReflectionPrompt[]>(
      `${ENDPOINT}/reflections/prompts`,
      { signal }
    );
    return response.data;
  },
  async dismissReflectionPrompt(prompt: ReflectionPrompt): Promise<void> {
    await api.post(`${ENDPOINT}/reflections/prompts/dismiss`, {
      library_entry_id: prompt.library_entry_id,
      reading_cycle_id: prompt.reading_cycle_id,
      prompt_id: prompt.prompt_id,
    });
  },
  async getReflectionHistory(id: number): Promise<ReflectionRevision[]> {
    const response = await api.get<ReflectionRevision[]>(
      `${ENDPOINT}/${id}/reflection-history`
    );
    return response.data;
  },
  async listAttachments(id: number): Promise<JournalAttachment[]> {
    const response = await api.get<JournalAttachment[]>(
      `${ENDPOINT}/${id}/attachments`
    );
    return response.data;
  },
  async uploadAttachment(
    id: number,
    file: File,
    description: string
  ): Promise<JournalAttachment> {
    const body = new FormData();
    body.append("file", file);
    body.append("description", description);
    const response = await api.post<JournalAttachment>(
      `${ENDPOINT}/${id}/attachments`,
      body
    );
    return response.data;
  },
  async deleteAttachment(entryId: number, attachmentId: number): Promise<void> {
    await api.delete(`${ENDPOINT}/${entryId}/attachments/${attachmentId}`);
  },
  async downloadAttachment(
    entryId: number,
    attachment: JournalAttachment
  ): Promise<void> {
    const response = await api.get<Blob>(
      `${ENDPOINT}/${entryId}/attachments/${attachment.id}/content`,
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = attachment.display_name;
    anchor.click();
    URL.revokeObjectURL(url);
  },
};
